import React, { useEffect, useState, useRef } from "react";
import {
  Palette, Plus, Upload, DollarSign, CheckCircle, XCircle, Clock, ArrowLeft,
  TrendingUp, Wallet as WalletIcon, Eye, Gift, ShieldCheck, Flower2, Brush,
  Camera, Share2, Sparkles,
} from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";
import { useNavigate, Link } from "react-router-dom";
import ModalShell from "@/components/ModalShell";
import { getVid } from "@/lib/vid";
import { optimizeImg } from "@/lib/img";

const STEPS = [
  { icon: Flower2, ar: ["اختر زهورك من RG", "اختر ما يناسب فكرتك من زهور ومنتجات RG."],
    en: ["Pick your RG flowers", "Choose the flowers & products that fit your idea."] },
  { icon: Brush, ar: ["أبدع بتصميمك", "اصنع تنسيقاً جديداً ومميزاً يعكس أسلوبك وإبداعك."],
    en: ["Create your design", "Craft a fresh, distinctive arrangement that reflects your style."] },
  { icon: Camera, ar: ["صوّر إبداعك", "التقط صوراً واضحة واحترافية للتصميم النهائي."],
    en: ["Photograph it", "Take clear, professional photos of the final design."] },
  { icon: Share2, ar: ["شارك تصميمك معنا", "ارفع الصور وبيانات التصميم من خلال «تصاميمي وأرباحي»."],
    en: ["Share it with us", "Upload the photos & details right here in Designs & Earnings."] },
  { icon: TrendingUp, ar: ["اعرض إبداعك واربح", "بعد المراجعة والاعتماد يُعرض لعملاء RG وتربح من كل عملية بيع."],
    en: ["Go live & earn", "Once approved, it goes live for RG customers and you earn per sale."] },
];

/* صفحة «تصاميمي وأرباحي» الموحدة: تصاميم العميل (بيع + AI المشتركة)
   + الأرباح + رصيد المحفظة وسجلها. */
export default function MyDesigns() {
  const { lang, formatPrice, user } = useApp();
  const ar = lang === "ar";
  const [designs, setDesigns] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [aiDesigns, setAiDesigns] = useState([]);
  const [commTiers, setCommTiers] = useState([
    { min_orders: 0, pct: 10 }, { min_orders: 5, pct: 15 }, { min_orders: 15, pct: 20 },
  ]);
  const [showUpload, setShowUpload] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [d, e] = await Promise.all([
        http.get("/designs/mine"),
        http.get("/designs/earnings"),
      ]);
      setDesigns(d.data || []);
      setEarnings(e.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => {
    if (user) http.get("/wallet/me").then(({ data }) => setWallet(data)).catch(() => {});
  }, [user]);
  useEffect(() => {
    http.get(`/ai/designs/mine?vid=${encodeURIComponent(getVid() || "")}`)
      .then(({ data }) => {
        setAiDesigns(data.designs || []);
        if (data.commission_tiers?.length) setCommTiers(data.commission_tiers);
      }).catch(() => {});
  }, [user]);

  const pctFor = (orders) => {
    let pct = commTiers[0]?.pct || 10;
    commTiers.forEach((t) => { if ((orders || 0) >= (t.min_orders || 0)) pct = t.pct; });
    return pct;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="my-designs-page">
      <button onClick={() => nav(-1)} className="chip" data-testid="designs-back">
        <ArrowLeft size={14} /> {ar ? "رجوع" : "Back"}
      </button>

      {/* المقدمة التعريفية */}
      <div className="rounded-2xl overflow-hidden p-5 sm:p-7 text-white"
           style={{ background: "linear-gradient(120deg, #0d3b2a, #105438 55%, #1a6b47)" }}
           data-testid="designs-hero">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1.5">RARE GROUP · Creators</div>
        <h1 className={`${ar ? "font-display-ar" : "font-display-en"} text-2xl sm:text-3xl leading-snug`}>
          {ar ? "تصاميمي وأرباحي" : "My Designs & Earnings"}
        </h1>
        <p className="mt-1 text-sm font-semibold text-amber-200">
          {ar ? "أبدع بزهور RG، شارك تصميمك، وابدأ بتحقيق أرباحك." : "Create with RG flowers, share your design, and start earning."}
        </p>
        <p className="mt-2 text-[13px] text-white/80 leading-relaxed max-w-2xl">
          {ar
            ? "حوّل زهور RG إلى تصميم يحمل لمستك الخاصة. ابتكر تنسيقاً جديداً، صوّر إبداعك، وارفعه إلى RG. بعد مراجعة التصميم واعتماده، يمكن إضافته إلى مجموعة RG وإتاحته للعملاء، وتحصل على أرباح من مبيعات تصميمك وفق شروط البرنامج."
            : "Turn RG flowers into a design with your personal touch. Create a new arrangement, photograph it and upload it to RG. Once reviewed and approved, it can join the RG collection — and you earn from every sale under the program terms."}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {user ? (
            <button onClick={() => setShowUpload(true)} className="btn-gold !min-h-0 !py-2 !px-4 !text-[13px]"
                    data-testid="design-upload-btn">
              <Plus size={14} /> {ar ? "ارفع تصميماً" : "Upload design"}
            </button>
          ) : (
            <Link to="/login?next=/my-designs" className="btn-gold !min-h-0 !py-2 !px-4 !text-[13px]"
                  data-testid="designs-login-btn">
              {ar ? "سجّل دخولك وابدأ" : "Sign in to start"}
            </Link>
          )}
        </div>
      </div>

      {/* رصيد المحفظة والأرباح */}
      {user && (
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <Link to="/wallet" className="card-soft p-3.5 block hover:shadow-md transition-shadow" data-testid="designs-wallet-card">
            <div className="flex items-center gap-1.5 text-[11px] opacity-60">
              <WalletIcon size={13} /> {ar ? "رصيد المحفظة" : "Wallet balance"}
            </div>
            <div className="text-xl font-bold mt-1 tabular-nums" style={{ color: "#105438" }}>
              {(wallet?.balance ?? 0).toLocaleString()} <span className="text-xs font-semibold opacity-60">{ar ? "نقطة" : "pts"}</span>
            </div>
            <div className="text-[11px] mt-0.5 font-semibold" style={{ color: "#9a6f37" }}>
              {wallet?.value_sar ? (ar ? `تعادل ${formatPrice(wallet.value_sar)} · ` : `≈ ${formatPrice(wallet.value_sar)} · `) : ""}{ar ? "سجل المحفظة ›" : "Wallet history ›"}
            </div>
          </Link>
          <div className="card-soft p-3.5" data-testid="earn-total">
            <div className="flex items-center gap-1.5 text-[11px] opacity-60">
              <TrendingUp size={13} /> {ar ? "إجمالي أرباح تصاميمي" : "Total design earnings"}
            </div>
            <div className="text-xl font-bold mt-1 tabular-nums" style={{ color: "#059669" }}>
              {formatPrice(earnings?.total_earned_sar || 0)}
            </div>
            <div className="text-[11px] mt-0.5 opacity-60">
              {ar ? `متاح للسحب: ${formatPrice(earnings?.available_sar || 0)}` : `Available: ${formatPrice(earnings?.available_sar || 0)}`}
            </div>
          </div>
        </div>
      )}

      {user && earnings && (
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <EarnCard label={ar ? "تصاميم منشورة" : "Live designs"}
                    value={earnings.by_status?.approved?.count || 0}
                    icon={CheckCircle} color="#059669" testid="earn-approved" />
          <EarnCard label={ar ? "قيد المراجعة" : "Pending review"}
                    value={earnings.by_status?.pending?.count || 0}
                    icon={Clock} testid="earn-pending" />
        </div>
      )}

      {/* 💸 سلم العمولات المتدرج */}
      <div className="card-soft p-4" style={{ background: "rgba(16,185,129,0.05)", borderColor: "rgba(16,185,129,0.25)" }}
           data-testid="commission-tiers-card">
        <div className="flex items-center gap-2 text-sm font-extrabold">
          <TrendingUp size={16} style={{ color: "#059669" }} />
          {ar ? "عمولتك ترتفع مع كل تصميم ناجح" : "Your commission grows per design"}
        </div>
        <div className="mt-2.5 flex items-stretch gap-2">
          {(earnings?.tiers || commTiers).slice(0, 3).map((t, i) => (
            <div key={i} className="flex-1 rounded-xl p-2.5 text-center border"
                 style={{ borderColor: "rgba(5,150,105,0.25)", background: "#fff" }}
                 data-testid={`commission-tier-${i + 1}`}>
              <div className="text-lg font-black" style={{ color: "#059669" }}>{Number(t.pct)}%</div>
              <div className="text-[10px] opacity-60 mt-0.5 font-semibold">
                {Number(t.min_orders || 0) === 0
                  ? (ar ? "من أول طلب" : "from 1st order")
                  : (ar ? `بعد ${t.min_orders} طلبات` : `after ${t.min_orders} orders`)}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] opacity-60 mt-2 leading-relaxed">
          {ar
            ? "تُحسب لكل تصميم على حدة، على قيمة الطلب، وتُضاف لمحفظتك بعد التسليم الناجح. المبالغ النقدية المرفقة مستثناة."
            : "Counted per design, on the order value, credited to your wallet after successful delivery. Attached cash gifts are excluded."}
        </p>
      </div>

      {/* تصاميم AI التي شاركها العميل */}
      {aiDesigns.length > 0 && (
        <section className="space-y-2.5" data-testid="my-ai-designs">
          <div className="flex items-center justify-between">
            <h2 className="font-bold flex items-center gap-1.5">
              <Sparkles size={15} className="text-emerald-700" />
              {ar ? "تصاميمي المشتركة بالذكاء" : "My shared AI designs"}
            </h2>
            <Link to="/designs" className="text-[12px] font-bold" style={{ color: "#2563eb" }}>
              {ar ? "معرض المجتمع ›" : "Community gallery ›"}
            </Link>
          </div>
          <div className="flex gap-2.5 overflow-x-auto scroll-x-hide pb-1">
            {aiDesigns.map((d) => (
              <Link key={d.id} to={`/design/${d.id}`}
                    className="shrink-0 w-[130px] rounded-xl overflow-hidden border bg-white"
                    style={{ borderColor: "var(--border)" }} data-testid={`ai-design-${d.id}`}>
                <img src={optimizeImg(d.image, 320)} alt="" loading="lazy" className="w-full aspect-square object-cover" />
                <div className="p-2">
                  <div className="text-[10.5px] font-semibold truncate">{d.label || (ar ? "تصميمي" : "My design")}</div>
                  <div className="flex items-center justify-between mt-0.5 text-[10px] opacity-60">
                    <span className="flex items-center gap-0.5"><Eye size={10} /> {d.views || 0}</span>
                    <span className="font-bold text-emerald-700">{pctFor(d.comm_orders)}%</span>
                  </div>
                  {(d.comm_orders || 0) > 0 && (
                    <div className="text-[9.5px] font-bold text-amber-600 mt-0.5" data-testid={`ai-design-earn-${d.id}`}>
                      🛒 {d.comm_orders} · +{(d.comm_points || 0).toLocaleString()} {ar ? "نقطة" : "pts"}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* تصاميم البيع المرفوعة */}
      {user && (
        <section className="space-y-2.5">
          <h2 className="font-bold flex items-center gap-1.5">
            <Palette size={15} className="text-emerald-700" />
            {ar ? "تصاميمي المعروضة للبيع" : "My designs for sale"}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {designs.length === 0 && (
              <div className="col-span-full p-6 text-center opacity-60 text-sm">
                {ar ? "لم ترفع تصاميم بعد — ابدأ بالخطوات أدناه." : "No designs uploaded yet — start with the steps below."}
              </div>
            )}
            {designs.map((d) => (
              <DesignCard key={d.id} design={d} lang={lang} formatPrice={formatPrice} pctFor={pctFor} />
            ))}
          </div>
        </section>
      )}

      {/* كيف تبدأ؟ */}
      <section className="space-y-3" data-testid="designs-how-to">
        <h2 className={`${ar ? "font-display-ar" : "font-display-en"} text-xl font-bold`}>
          {ar ? "كيف تبدأ؟" : "How to start"}
        </h2>
        <div className="space-y-2">
          {STEPS.map((s, i) => {
            const [title, desc] = ar ? s.ar : s.en;
            return (
              <div key={i} className="card-soft p-3.5 flex items-start gap-3" data-testid={`designs-step-${i + 1}`}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                     style={{ background: "rgba(16,84,56,0.08)", color: "#105438" }}>
                  <s.icon size={17} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold">{i + 1}. {title}</div>
                  <div className="text-[12.5px] opacity-70 mt-0.5 leading-relaxed">{desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* الأصالة أساس البرنامج */}
      <section className="card-soft p-4 sm:p-5 flex items-start gap-3"
               style={{ background: "rgba(176,141,77,0.07)", borderColor: "rgba(176,141,77,0.3)" }}
               data-testid="designs-originality">
        <ShieldCheck size={22} className="shrink-0 mt-0.5" style={{ color: "#9a6f37" }} />
        <div>
          <div className="font-bold text-sm">{ar ? "الأصالة أساس البرنامج — نبحث عن إبداعك أنت." : "Originality is the core — we want YOUR creativity."}</div>
          <p className="text-[12.5px] opacity-75 mt-1 leading-relaxed">
            {ar
              ? "يجب أن يكون التصميم جديداً ومن ابتكارك، وألا يكون منسوخاً أو معاد تنفيذه من تصميم موجود على Instagram أو TikTok أو Pinterest أو أي متجر أو منصة أخرى."
              : "Your design must be new and your own creation — not copied or re-made from any design on Instagram, TikTok, Pinterest or any other store or platform."}
          </p>
          <div className="mt-2 text-[13px] font-extrabold" style={{ color: "#105438" }}>
            {ar ? "ابتكرها. صمّمها. شاركها. واربح منها." : "Imagine it. Design it. Share it. Earn from it."}
          </div>
        </div>
      </section>

      {showUpload && (
        <UploadModal lang={lang} onClose={() => setShowUpload(false)}
                      onDone={() => { setShowUpload(false); load(); }} />
      )}
    </div>
  );
}

function DesignCard({ design, lang, formatPrice, pctFor }) {
  const statusColor = {
    pending: "#d97706",
    approved: "#059669",
    rejected: "#dc2626",
  }[design.status] || "#6b7280";
  const statusIcon = {
    pending: Clock,
    approved: CheckCircle,
    rejected: XCircle,
  }[design.status] || Clock;
  const StatusIcon = statusIcon;
  const statusLabel = {
    pending: lang === "ar" ? "قيد المراجعة" : "Pending",
    approved: lang === "ar" ? "منشور" : "Live",
    rejected: lang === "ar" ? "مرفوض" : "Rejected",
  }[design.status];

  return (
    <div className="card-soft overflow-hidden" data-testid={`design-${design.id}`}>
      <img src={design.image} alt={design.title_en} className="w-full aspect-square object-contain bg-white" />
      <div className="p-3 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold truncate">{lang === "ar" ? design.title_ar : design.title_en}</div>
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full"
                style={{ background: `${statusColor}20`, color: statusColor }}>
            <StatusIcon size={10} /> {statusLabel}
          </span>
        </div>
        <div className="flex items-center justify-between text-xs opacity-70">
          <span>{formatPrice(design.price_sar)}</span>
          <span>× {design.total_sales} · {formatPrice(design.total_earned_sar)}</span>
        </div>
        {design.status === "approved" && pctFor && (
          <div className="text-[10.5px] font-bold text-emerald-700" data-testid={`design-pct-${design.id}`}>
            {lang === "ar"
              ? `عمولتك الحالية: ${pctFor(design.comm_orders)}% (${design.comm_orders || 0} طلب مُسلَّم)`
              : `Current commission: ${pctFor(design.comm_orders)}% (${design.comm_orders || 0} delivered)`}
          </div>
        )}
        {design.status === "rejected" && design.rejection_reason && (
          <div className="text-[11px] text-red-600 mt-1">
            {lang === "ar" ? "السبب:" : "Reason:"} {design.rejection_reason}
          </div>
        )}
      </div>
    </div>
  );
}

function EarnCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="card-soft p-3" data-testid={testid}>
      <div className="flex items-center gap-1 text-[11px] opacity-60">
        <Icon size={12} /> {label}
      </div>
      <div className="text-xl font-bold mt-1 tabular-nums" style={color ? { color } : undefined}>
        {value}
      </div>
    </div>
  );
}

function UploadModal({ lang, onClose, onDone }) {
  const [form, setForm] = useState({
    title_ar: "", title_en: "", description_ar: "", description_en: "",
    price_sar: 250, category_slug: "bouquets", image_base64: "", tags: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) { setErr(lang === "ar" ? "أكبر من 6MB" : "Too big (>6MB)"); return; }
    const r = new FileReader();
    r.onload = () => setForm((x) => ({ ...x, image_base64: r.result }));
    r.readAsDataURL(f);
  };

  const submit = async () => {
    setErr("");
    if (!form.title_ar.trim() || !form.title_en.trim() || !form.image_base64) {
      setErr(lang === "ar" ? "الحقول الإلزامية: العنوان AR/EN + الصورة" : "Required: title AR/EN + image");
      return;
    }
    setBusy(true);
    try {
      await http.post("/designs/submit", {
        ...form,
        price_sar: Number(form.price_sar),
        tags: form.tags.split(",").map((x) => x.trim()).filter(Boolean),
      });
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} testId="upload-design-modal">
      <div className="flex items-center gap-2 mb-2">
        <Upload size={16} />
        <div className="font-semibold">{lang === "ar" ? "رفع تصميم جديد" : "Upload new design"}</div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={pickFile}
             className="hidden" data-testid="upload-design-file" />
      {!form.image_base64 ? (
        <button onClick={() => fileRef.current?.click()}
                className="w-full py-6 rounded-xl border-2 border-dashed flex flex-col items-center gap-2"
                style={{ borderColor: "var(--border)" }}
                data-testid="upload-design-pick">
          <Upload size={24} className="opacity-60" />
          <span className="text-sm">{lang === "ar" ? "اختر صورة التصميم" : "Pick design image"}</span>
        </button>
      ) : (
        <img src={form.image_base64} alt="design" className="w-full aspect-square object-contain bg-white rounded-xl" />
      )}
      <div className="grid grid-cols-2 gap-2">
        <input className="input-field" placeholder={lang === "ar" ? "اسم بالعربية" : "Title AR"}
               value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })}
               data-testid="upload-title-ar" />
        <input className="input-field" placeholder="Title EN"
               value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })}
               data-testid="upload-title-en" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input className="input-field" type="number" min="1" max="5000"
               placeholder={lang === "ar" ? "السعر ر.ق" : "Price QAR"}
               value={form.price_sar} onChange={(e) => setForm({ ...form, price_sar: e.target.value })}
               data-testid="upload-price" />
        <select className="input-field" value={form.category_slug}
                onChange={(e) => setForm({ ...form, category_slug: e.target.value })}
                data-testid="upload-category">
          <option value="bouquets">{lang === "ar" ? "باقات" : "Bouquets"}</option>
          <option value="gifts">{lang === "ar" ? "هدايا" : "Gifts"}</option>
          <option value="cakes">{lang === "ar" ? "كيك" : "Cakes"}</option>
          <option value="eid">{lang === "ar" ? "عيد" : "Eid"}</option>
        </select>
      </div>
      <input className="input-field" placeholder={lang === "ar" ? "كلمات مفتاحية (مفصولة بفاصلة)" : "Tags (comma-separated)"}
             value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
      <textarea className="input-field !text-sm" rows={2}
                placeholder={lang === "ar" ? "وصف بالعربية (اختياري)" : "Description AR (optional)"}
                value={form.description_ar}
                onChange={(e) => setForm({ ...form, description_ar: e.target.value })} />
      {err && <div className="text-sm text-red-600">{err}</div>}
      <div className="flex gap-2 pt-2">
        <button onClick={onClose} className="btn-outline flex-1">
          {lang === "ar" ? "إلغاء" : "Cancel"}
        </button>
        <button onClick={submit} disabled={busy} className="btn-primary flex-1"
                data-testid="upload-design-submit">
          {busy
            ? (lang === "ar" ? "جاري الرفع..." : "Uploading...")
            : (lang === "ar" ? "ارفع للمراجعة" : "Submit for review")}
        </button>
      </div>
    </ModalShell>
  );
}
